/* eslint-disable @typescript-eslint/naming-convention */
import { createContext, Dispatch, PropsWithChildren, SetStateAction, useContext, useEffect, useState } from 'react'

import { AddPlayerFormData, CurrentActivePlayer, PlayerPosition } from '@common/types'
import { removeFirstOccurrence } from '@common/utils'

type AddPlayerContextValue = AddPlayerContextProps & {
    newPlayer: AddPlayerFormData
    setNewPlayer: Dispatch<SetStateAction<AddPlayerFormData>>
    isSurnameInvalid: boolean
    takenShirtNumWithoutExistingPlayer: number[]
    isSaveDisabled: boolean
    handleSavePlayer: () => void
    isEditPlayer: boolean
    isFakePlayer: boolean
    isRealPlayer: boolean
    isFrontFakePlayer: boolean
    patchPlayer: (patchValues: Partial<AddPlayerFormData>) => void
    resetNewPlayer: () => void
}

export type AddPlayerContextProps = {
    onAddNewPlayer: (newPlayerData: PlayerPosition) => void
    currentActivePlayer?: CurrentActivePlayer
    takenShirtNumbers?: number[]
    isExistingPlayer?: boolean
    isSquadPlayer?: boolean
    enableCaptainEdit?: boolean
    isSubstitute: boolean
}

const defaultNewPlayer = {
    id: null,
    name: undefined,
    surname: '',
    shirt_number: undefined,
    captain: false,
}

const defaultValue: AddPlayerContextValue = {
    onAddNewPlayer: () => undefined,
    currentActivePlayer: undefined,
    takenShirtNumbers: [],
    isExistingPlayer: undefined,
    isSquadPlayer: undefined,
    enableCaptainEdit: undefined,
    isSubstitute: false,
    newPlayer: defaultNewPlayer,
    setNewPlayer: () => undefined,
    isSurnameInvalid: false,
    takenShirtNumWithoutExistingPlayer: [],
    isSaveDisabled: false,
    handleSavePlayer: () => undefined,
    isEditPlayer: false,
    isFakePlayer: false,
    isRealPlayer: false,
    isFrontFakePlayer: false,
    patchPlayer: () => undefined,
    resetNewPlayer: () => undefined,
}

const generatePlayerID = () => `front:fake:${Math.floor(1000 + Math.random() * 9000)}`

export const AddPlayerContext = createContext<AddPlayerContextValue>(defaultValue)

AddPlayerContext.displayName = 'AddPlayerContext'

export function AddPlayerProvider({ children, ...props }: PropsWithChildren<AddPlayerContextProps>) {
    const { onAddNewPlayer, currentActivePlayer, takenShirtNumbers = [], isExistingPlayer } = props
    const [newPlayer, setNewPlayer] = useState<AddPlayerFormData>(defaultNewPlayer)

    const resetNewPlayer = () => {
        setNewPlayer({
            ...defaultNewPlayer,
            id: currentActivePlayer?.id ?? generatePlayerID(),
            name: '',
            surname: currentActivePlayer?.name ?? '',
            shirt_number: currentActivePlayer?.shirt_number,
            captain: currentActivePlayer?.captain ?? false,
        })
    }

    useEffect(() => {
        setNewPlayer({
            ...defaultNewPlayer,
            id: currentActivePlayer?.id ?? generatePlayerID(),
            name: '',
            surname: currentActivePlayer?.name ?? '',
            shirt_number: currentActivePlayer?.shirt_number,
            captain: currentActivePlayer?.captain ?? false,
        })
    }, [currentActivePlayer])

    const isSurnameInvalid = !newPlayer?.surname?.trim().length

    const takenShirtNumWithoutExistingPlayer =
        isExistingPlayer && currentActivePlayer?.shirt_number && typeof currentActivePlayer?.shirt_number === 'number'
            ? removeFirstOccurrence(takenShirtNumbers, currentActivePlayer?.shirt_number)
            : takenShirtNumbers

    const isSaveDisabled = isSurnameInvalid

    const handleSavePlayer = () => {
        if (currentActivePlayer?.colIndex !== undefined && currentActivePlayer?.rowIndex !== undefined) {
            onAddNewPlayer({
                player: {
                    id: newPlayer.id,
                    name: newPlayer.surname,
                    shirt_number: newPlayer.shirt_number,
                    captain: newPlayer.captain,
                },
                position: {
                    rowIndex: currentActivePlayer.rowIndex,
                    colIndex: currentActivePlayer.colIndex,
                },
            })
        } else if (currentActivePlayer?.index !== undefined) {
            onAddNewPlayer({
                player: {
                    id: newPlayer.id,
                    name: newPlayer.surname,
                    shirt_number: newPlayer.shirt_number,
                    captain: false,
                },
                position: {
                    index: currentActivePlayer.index,
                },
            })
        } else if (currentActivePlayer?.reason !== undefined) {
            onAddNewPlayer({
                player: {
                    ...currentActivePlayer,
                    ...{
                        id: newPlayer.id,
                        name: newPlayer.surname,
                        shirt_number: newPlayer.shirt_number,
                        captain: newPlayer.captain,
                    },
                },
            })
        } else {
            onAddNewPlayer({
                player: {
                    ...currentActivePlayer,
                    ...{
                        name: newPlayer.surname,
                        shirt_number: newPlayer.shirt_number,
                        captain: newPlayer.captain,
                    },
                },
            })
        }
    }

    const isEditPlayer = Boolean(currentActivePlayer?.name)
    const isFakePlayer = Boolean(currentActivePlayer?.id?.includes('fake'))
    const isFrontFakePlayer = Boolean(newPlayer?.id && newPlayer?.id.startsWith('front:fake:'))
    const isRealPlayer = Boolean(currentActivePlayer?.id && !currentActivePlayer?.id.includes('fake'))

    const patchPlayer = (patchValues: Partial<AddPlayerFormData>) => {
        setNewPlayer(previousNewPlayer => ({ ...previousNewPlayer, ...patchValues }))
    }

    const contextValue = {
        ...props,
        newPlayer,
        setNewPlayer,
        isSurnameInvalid,
        takenShirtNumWithoutExistingPlayer,
        isSaveDisabled,
        handleSavePlayer,
        isEditPlayer,
        isFakePlayer,
        isFrontFakePlayer,
        isRealPlayer,
        patchPlayer,
        resetNewPlayer,
    }

    return <AddPlayerContext.Provider value={contextValue}>{children}</AddPlayerContext.Provider>
}

export function useAddPlayerContext() {
    return useContext(AddPlayerContext)
}
